/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, FormGroup, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useServiceContext } from '../../root'
import Field from '../shared/Field'
import { ErrorMessageStyled } from '../shared/ErrorMessage'
import Modal from '../Modal'

const StyledModalWrapper = styled.div`
  .modal {
    z-index: 9999 !important;
  }

  .modal-backdrop,
  .esig-modal-backdrop {
    z-index: 9998 !important;
  }
`

/**
 * Props for RcmEsignatureModal component
 */
interface RcmEsignatureModalProps {
  /** Callback when form is submitted with validation credentials */
  onSubmit: (validation: { username?: string; password: string }) => void
  /** Callback when modal is closed/cancelled */
  onClose: () => void
  /** Message to display above the form fields */
  esignMessage: string
}

/**
 * Form values interface
 */
interface EsignFormValues {
  username?: string
  password: string
}

/**
 * eSignature Modal Component
 *
 * This modal collects user credentials for eSignature authentication.
 * Conditionally shows username field based on auth configuration.
 *
 * Migrated from: lego/component/esignature/RcmEsignatureModal.jsx
 */
const RcmEsignatureModal: React.FC<RcmEsignatureModalProps> = ({ onSubmit, onClose, esignMessage }) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const [isOpen, setIsOpen] = useState(true) // Initialize to true immediately
  const [showEmailInput, setShowEmailInput] = useState(false)

  // Check if username field is required
  useEffect(() => {
    setShowEmailInput(context.service.auth.requireUsernameInESignature())
  }, [context.service.auth])

  /**
   * Close modal and notify parent
   */
  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  /**
   * Submit form with validation values and close modal
   */
  const doSave = (values: EsignFormValues) => {
    onSubmit(values as { username?: string; password: string })
    setIsOpen(false)
  }

  return (
    <StyledModalWrapper>
      <Modal
        show={isOpen}
        className="esig-modal"
        handleClose={doClose}
        style={{ zIndex: 9999 }}
        title={t('ESIGNATURE_MODAL_TITLE')}
      >
        <Formik<EsignFormValues>
          initialValues={
            showEmailInput
              ? {
                  username: '',
                  password: ''
                }
              : { password: '' }
          }
          enableReinitialize
          validate={(values) => {
            const errors: Partial<Record<keyof EsignFormValues, string>> = {}

            // Validate username if field is shown
            if (showEmailInput) {
              if (values.username?.trim() === '' || !values.username) {
                errors.username = 'SINGIN_EMAIL_NEEDED'
              } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.username)) {
                errors.username = 'INVALID_BOX_EMAIL_MSG'
              }
            }

            // Validate password (always required)
            if (values.password.trim() === '') {
              errors.password = 'SINGIN_PSW_NEEDED'
            }

            return errors
          }}
          onSubmit={(values) => {
            doSave(values)
          }}
        >
          {({ handleSubmit }) => (
            <>
              <Modal.Body>
                {/* Display message above form fields */}
                {esignMessage && (
                  <p className="word-break" style={{ paddingBottom: '15px' }}>
                    {esignMessage}
                  </p>
                )}

                {/* Conditional username/email field */}
                {showEmailInput && (
                  <FormGroup controlId="ea903951-f8ec-45e3-8841-d2aa428d74c9" className="w100p">
                    <Form.Label>{t('EMAIL_ADDRESS')}</Form.Label>
                    <Field
                      type="email"
                      className="form-control field-control"
                      name="username"
                      id="ea903951-f8ec-45e3-8841-d2aa428d74c9"
                      autoFocus
                    />
                    <ErrorMessageStyled name="username" />
                  </FormGroup>
                )}

                {/* Password field (always shown) */}
                <FormGroup controlId="9a0a3921-db6a-4016-97c8-9b3d9ac4832c" className="w100p">
                  <Form.Label>{t('USERS_FORM_LABEL_PASSWORD')}</Form.Label>
                  <Field
                    type="password"
                    className="form-control field-control"
                    name="password"
                    id="9a0a3921-db6a-4016-97c8-9b3d9ac4832c"
                    autoFocus={!showEmailInput}
                  />
                  <ErrorMessageStyled name="password" />
                </FormGroup>
              </Modal.Body>

              <Modal.Footer>
                {/* Cancel button */}
                <Button
                  data-action="cancel"
                  type="button"
                  variant="link"
                  onClick={doClose}
                  id="f618e70b-cbaa-4671-a8c0-520b01685039"
                >
                  {t('CANCEL')}
                </Button>

                {/* Submit/OK button */}
                <Button
                  data-action="ok"
                  className="btn btn-primary text-capitalize"
                  onClick={() => {
                    handleSubmit()
                  }}
                  id="9d3526c1-7228-4b0c-9931-dd4b5d4def92"
                >
                  {t('OK')}
                </Button>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>
    </StyledModalWrapper>
  )
}

export default RcmEsignatureModal
